import "../../../../../../lib/assets/javascripts/assert.js";
import "../../../utilities/scroll_to_top.js";
import "../../../shared/accessibility.js";
import "../../../shared/events.js";

window.AppointmentBooking ??= {};
window.AppointmentBooking.SlotSuggester ??= {};
window.AppointmentBooking.SlotSuggester.Views ??= {};

AppointmentBooking.SlotSuggester.Views.Base = class Base extends Backbone.View {
  constructor() {
    super(...arguments);
    this.preinitialize = this.preinitialize.bind(this);
    this.initialize = this.initialize.bind(this);
    this.ensureRendered = this.ensureRendered.bind(this);
    this.setRenderable = this.setRenderable.bind(this);
    this.render = this.render.bind(this);
    this.renderTemplate = this.renderTemplate.bind(this);
    this.contextForRender = this.contextForRender.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.toggle = this.toggle.bind(this);
    this.renderPartial = this.renderPartial.bind(this);
    this.I18n = this.I18n.bind(this);
    this.clearContents = this.clearContents.bind(this);
    this.replaceContents = this.replaceContents.bind(this);
    this.triggerEvent = this.triggerEvent.bind(this);
    this.eachAncestor = this.eachAncestor.bind(this);
    this.generateErrorReport = this.generateErrorReport.bind(this);
    this.trackViewAnalytics = this.trackViewAnalytics.bind(this);
    this.resetViewDetection = this.resetViewDetection.bind(this);
  }

  preinitialize() {
    super.preinitialize(...arguments);

    // Set up namespace shortcuts
    this.Views = this.constructor;
    this.Events = AppointmentBooking.SlotSuggester.Events;
    this.Models = AppointmentBooking.SlotSuggester.Models;
    this.Collections = AppointmentBooking.SlotSuggester.Collections;

    this.eachAncestor((ancestorProtoype) => {
      // Inherit events from superclass
      this.events = {
        ...ancestorProtoype.events,
        ...this.events,
      };

      // Inherit class names from superclass
      const ancestorClassName = ancestorProtoype.className;
      if (ancestorClassName && ancestorClassName !== this.className) {
        return (this.className = jQuery.trim(`${ancestorClassName} ${this.className}`));
      }
    });

    // Make it easy for views to assert things
    // Backbone calls preinitialize from inside its own constructor, so this runs before the bind
    // statements above. The closure resolves `this` when the Asserter calls it, not now.
    this.assert = new Asserter({ notifyUser: false, reportData: () => this.generateErrorReport() });

    // Ensure a template is defined
    if (this.template !== false) {
      return this.assert.presence(this.template, `No template defined for ${this.constructor.name}`);
    }
  }

  initialize(options = {}) {
    super.initialize(options);

    // Allow setting of templates at runtime for classes that have opted out of hardcoded templates
    if (this.template === false) {
      if (options.template != null) {
        this.template = options.template;
      }
    } else {
      this.assert.condition(
        options.template == null,
        `${this.constructor.name} already defines a template. Cannot override template at runtime.`,
      );
    }

    // Retain class-level css class name even if we specify an additional one at runtime
    const classClassName = this.constructor.prototype.className;
    if (classClassName && classClassName !== this.className) {
      this.className = this.$el.addClass(classClassName);
    }
    window.Sparkle.Accessibility.clickableWithKeyboard(this.$el);

    // Enable analytics
    if (options.analytics === true) {
      this.trackViewAnalytics("view-shown");
    } else if (options.analytics) {
      this.trackViewAnalytics(options.analytics);
    }

    // BEHAVIOUR

    if (options.parent) {
      // Match parent view renderability
      return this.listenTo(options.parent, "change:renderable", (state) => this.setRenderable(state));
    }
  }

  // RENDERING

  ensureRendered() {
    if (!this.rendered) {
      const { renderable } = this;
      this.setRenderable(true);
      this.render();
      this.setRenderable(renderable);
    }

    return this;
  }

  setRenderable(state) {
    if (state !== this.renderable) {
      this.renderable = state;
      return this.trigger("change:renderable", this.renderable);
    }
  }

  render() {
    if (!this.renderable) {
      return this;
    }

    this.resetViewDetection(); // Reset the state of the visibility check manually in case the content has changed
    this.rendered = true;
    if (this.template !== false) {
      this.replaceContents(this.$el, this.renderTemplate(this.contextForRender()));
    }
    Sparkle.Events.contentUpdated(this.el);

    return this;
  }

  renderTemplate(context) {
    const path = `appointment_booking/slot_suggester/templates/${this.template}`;
    const JST = this.assert.presence(window.JST, "JST is not loaded");
    const template = this.assert.presence(JST[path], `JST Template not found: ${path}`);

    try {
      return template(context);
    } catch (error) {
      return this.assert.failAssertion(
        `AppointmentBooking.SlotSuggester failed to render template '${template}': ${error.message}`,
      );
    }
  }

  contextForRender() {
    const context = {};
    jQuery.extend(
      context,
      typeof this.model?.defaults === "function" ? this.model.defaults() : undefined,
      this.model != null ? this.model.toJSON() : undefined,
    ); // Defaults are included in case a model attribute has been set to `undefined`, which would cause the key not to appear in the context sent to the template.
    context["I18n"] = this.I18n();
    context["renderPartial"] = this.renderPartial;
    return context;
  }

  show() {
    this.$el.show();
    return this;
  }

  hide() {
    this.$el.hide();
    return this;
  }

  toggle(state) {
    this.$el.toggle(!!state);
    return this;
  }

  // PRIVATE

  renderPartial(name, locals) {
    const context = { I18n: this.I18n(), renderPartial: this.renderPartial };
    _.keys(locals).forEach((key) => (context[key] = locals[key])); // Custom extend that ensures even incoming keys with `undefined` values still appear as local variables. Neither jQuery and Underscore have method with this behaviour.
    return JST[name](context);
  }

  I18n() {
    return AppointmentBooking.config.i18n.slot_suggester;
  }

  clearContents(target = this.el) {
    return this.replaceContents(target, "");
  }

  replaceContents(target, ...newContents) {
    target = typeof target === "string" ? this.$el.find(target) : jQuery(target);
    target.children().detach(); // Don't blow away event handlers on subviews
    target.empty(); // Remove any text nodes
    return newContents.forEach((content) => target.append(content));
  }

  // Used to communicate UI interaction events up to parent views
  triggerEvent(event, ...payload) {
    this.$el.trigger(event, payload);
    return this.trigger(event, payload);
  }

  eachAncestor(fn) {
    let proto = Object.getPrototypeOf(this.constructor.prototype);
    const result = [];
    while (proto && proto.constructor !== Backbone.View) {
      result.push(fn(proto));
      proto = Object.getPrototypeOf(proto);
    }
    return result;
  }

  generateErrorReport() {
    return { model: this.model.toJSON() };
  }

  scrollTo(element, options) {
    options = jQuery.extend({ ifNecessary: true, duration: 0, padding: 50 }, options);
    return Sparkle.Utilities.scrollToTop(element, options);
  }

  // Repeatedly check if the view was just made visible. This makes the check resilient to multiple show() calls as
  // the view state resolves, as well as parent containers hiding and showing without directly hiding the view itself.
  trackViewAnalytics(eventName) {
    this.wasVisible = false;
    return setInterval(() => {
      const isVisible = this.$el.is(":visible");
      if (!this.wasVisible && isVisible) {
        this.wasVisible = true;
        return this.$el.trigger("appointment-booking-slot-reselector-analytics", [eventName]);
      } else if (!isVisible) {
        return this.resetViewDetection();
      }
    }, 1000);
  }

  resetViewDetection() {
    return (this.wasVisible = false);
  }

  // Syntactic sugar for defining properties
  static prop(...args) {
    return Object.defineProperty(this.prototype, ...Array.from(args));
  }

  static get(propName, getter) {
    return Object.defineProperty(this.prototype, propName, { get: getter });
  }
};
AppointmentBooking.SlotSuggester.Views.Base.prototype.renderable = true;
